import type { ColumnDef } from '@tanstack/react-table';
import { useState, useMemo, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import DataGridTable from '@components/DataGridTable/DataGridTable';
import Icon from '@components/common/Icon';
import Toggle from '@components/common/Toggle';
import {
    TableRowActionsTooltipProvider,
    TableRowIconActions,
} from '@components/common/TableRowIconActions';
import Button from '@components/Button/Button';
import Select from '@components/Form/Select/Select';
import Input from '@/components/Form/Input/Input';
import PageHeader from '@components/common/PageHeader';
import ConfirmModal from '@components/common/ConfirmModal/ConfirmModal';
import cohortsService, { type CohortDatum } from '@/services/cohorts/cohortsService';
import { useAuth } from '@/context/AuthContext';
import { getApiErrorMessage } from '@/utils/apiValidationErrors';
import AssignStudentsModal from '@/components/Cohorts/AssignStudentsModal';
import CohortStudentsModal from '@/components/Cohorts/CohortStudentsModal';


const CohortsPage = () => {
    const { t } = useTranslation('cohorts');
    const navigate = useNavigate();
    const [assignCohort, setAssignCohort] = useState<{ id: string; name: string } | null>(null);
    const [viewCohortStudents, setViewCohortStudents] = useState<{ id: string; name: string } | null>(null);

    const queryClient = useQueryClient();
    const { isSuperAdmin } = useAuth();

    const statusOptions = useMemo(() => [
        { value: '', label: t('filters.all_status') },
        { value: 'ACTIVE', label: t('filters.active') },
        { value: 'INACTIVE', label: t('filters.inactive') },
        { value: 'COMPLETED', label: t('filters.completed') },
    ], [t]);

    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [pageIndex, setPageIndex] = useState(0);
    const [debouncedSearch, setDebouncedSearch] = useState(search);
    const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null);
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
    const PAGE_SIZE = 20;

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(timer);
    }, [search]);

    const listQueryKey = ['cohorts', pageIndex, PAGE_SIZE, statusFilter, debouncedSearch];

    const { data: cohortsResponse, isLoading, isError } = useQuery({
        queryKey: listQueryKey,
        queryFn: () =>
            cohortsService.getCohorts(
                pageIndex + 1,
                PAGE_SIZE,
                debouncedSearch.trim() || undefined,
                undefined, // courseId
                statusFilter || undefined,
            ),
        placeholderData: previousData => previousData,
        staleTime: 5 * 60 * 1000,
    });

    const cohorts = useMemo<CohortDatum[]>(
        () => cohortsResponse?.data?.data ?? [],
        [cohortsResponse],
    );

    const toggleCohortStatusMutation = useMutation({
        mutationFn: ({ cohort, nextStatus }: { cohort: CohortDatum; nextStatus: string }) =>
            cohortsService.updateCohort(cohort.id, {
                status: nextStatus as any,
            }),
        onMutate: async ({ cohort, nextStatus }) => {
            await queryClient.cancelQueries({ queryKey: listQueryKey });
            const previousResponse = queryClient.getQueryData(listQueryKey);

            queryClient.setQueryData(listQueryKey, (oldData: typeof cohortsResponse) => {
                if (!oldData?.data?.data) return oldData;
                return {
                    ...oldData,
                    data: {
                        ...oldData.data,
                        data: oldData.data.data.map(item =>
                            item.id === cohort.id ? { ...item, status: nextStatus as any } : item,
                        ),
                    },
                };
            });

            return { previousResponse };
        },
        onSuccess: (response) => {
            toast.success(response.message ?? t('toast.status_success'));
            queryClient.invalidateQueries({ queryKey: ['cohorts'] });
        },
        onError: (error: unknown, _variables, context) => {
            if (context?.previousResponse) {
                queryClient.setQueryData(listQueryKey, context.previousResponse);
            }
            toast.error(getApiErrorMessage(error, t('toast.status_error')));
        },
        onSettled: () => {
            setStatusUpdatingId(null);
        },
    });

    const deleteCohortMutation = useMutation({
        mutationFn: (id: string) => cohortsService.deleteCohort(id),
        onSuccess: (response) => {
            toast.success(response.message ?? t('toast.delete_success'));
            setPendingDeleteId(null);
            queryClient.invalidateQueries({ queryKey: ['cohorts'] });
        },
        onError: (error: unknown) => {
            toast.error(getApiErrorMessage(error, t('toast.delete_error')));
        },
    });

    const columns = useMemo<ColumnDef<CohortDatum>[]>(() => {
        const cols: ColumnDef<CohortDatum>[] = [
            {
                accessorKey: 'cohort_name',
                header: t('table.cohort_title'),
                size: 260,
                cell: ({ row }) => (
                    <div className="flex flex-col">
                        <span className="font-semibold text-[var(--secondary-color)]">
                            {row.original.cohort_name}
                        </span>
                        <span className="text-xs text-[var(--disabled-text-color)] mt-0.5">
                            {row.original.course?.course_name ?? 'AI Fellowship Program'}
                        </span>
                    </div>
                ),
            },
            {
                id: 'dates',
                header: t('table.dates'),
                size: 320,
                cell: ({ row }) => {
                    const formatDate = (dateStr: string) =>
                        new Date(dateStr).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                        });
                    return (
                        <div className="flex flex-col text-xs text-[var(--secondary-color)] gap-1">
                            <div>
                                <span className="font-medium text-[var(--disabled-text-color)]">Registration: </span>
                                {formatDate(row.original.registration_start)} - {formatDate(row.original.registration_end)}
                            </div>
                            <div>
                                <span className="font-medium text-[var(--disabled-text-color)]">Execution Start: </span>
                                {formatDate(row.original.start_date)}
                            </div>
                        </div>
                    );
                },
            },
            {
                accessorKey: 'duration',
                header: t('table.duration'),
                size: 130,
                cell: ({ row }) => (
                    <span className="text-sm font-medium text-[var(--secondary-color)]">
                        {row.original.duration} days
                    </span>
                ),
            },
            {
                accessorKey: 'status',
                header: t('table.status'),
                size: 160,
                enableSorting: false,
                cell: ({ row }) => {
                    const currentStatus = row.original.status;
                    const isActive = currentStatus === 'ACTIVE';

                    return (
                        <div className="flex items-center gap-2">
                            <Toggle
                                active={isActive}
                                disabled={statusUpdatingId === row.original.id}
                                onChange={(nextActive) => {
                                    setStatusUpdatingId(row.original.id);
                                    toggleCohortStatusMutation.mutate({
                                        cohort: row.original,
                                        nextStatus: nextActive ? 'ACTIVE' : 'INACTIVE',
                                    });
                                }}
                            />
                            {currentStatus === 'COMPLETED' && (
                                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">
                                    {t('filters.completed')}
                                </span>
                            )}
                        </div>
                    );
                },
            },
            {
                id: 'actions',
                header: t('table.action'),
                size: 160,
                minSize: 160,
                enableSorting: false,
                cell: ({ row }) => {
                    const cohort = row.original;
                    const hasStarted = new Date() >= new Date(cohort.start_date);

                    const actions = [
                        {
                            icon: 'eyeShowPrimary' as const,
                            label: t('actions.view'),
                            onClick: () => navigate(`/admin/cohorts/${cohort.id}`),
                        },
                        {
                            icon: 'teamassignemeticon' as const,
                            label: 'Assign Students',
                            onClick: () => setAssignCohort({ id: cohort.id, name: cohort.generated_cohort_title }),
                        },
                        {
                            icon: 'users' as const,
                            label: 'View Roster & Payments',
                            onClick: () => setViewCohortStudents({ id: cohort.id, name: cohort.generated_cohort_title }),
                        },
                        {
                            icon: 'edit' as const,
                            label: t('actions.edit'),
                            onClick: () => navigate(`/admin/cohorts/${cohort.id}`),
                        },
                        {
                            icon: 'delete' as const,
                            label: hasStarted ? 'Cannot Delete (Cohort Started)' : t('actions.delete'),
                            onClick: () => setPendingDeleteId(cohort.id),
                            variant: 'danger' as const,
                            disabled: hasStarted,
                        },
                    ];
                    return <TableRowIconActions actions={actions} />;
                },
            },
        ];

        return cols;
    }, [navigate, statusUpdatingId, toggleCohortStatusMutation, t, setAssignCohort, setViewCohortStudents]);

    const searchIcon = (
        <button className="flex items-center cursor-pointer">
            <Icon name='search' className='w-3 2xl:w-[14px] h-auto' />
        </button>
    );

    return (
        <TableRowActionsTooltipProvider>
            <>
                <PageHeader
                    title={t('page_title')}
                    breadcrumbItems={[
                        { label: t('breadcrumb'), to: '/admin/dashboard' },
                        { label: t('breadcrumb_cohort') },
                    ]}
                    actions={
                        <>
                            {isSuperAdmin && (
                                <Button onClick={() => navigate('/admin/cohorts/create')}>
                                    <Icon name="plus" className='mr-1 md:mr-2 mt-[-1px] w-3 2xl:w-4 h-auto' />
                                    {t('buttons.create')}
                                </Button>
                            )}
                        </>
                    }
                />
                <div className="flex flex-col gap-4 2xl:gap-5 !pt-0 p-3 md:p-4 2xl:p-5">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 lg:gap-4 2xl:gap-5 p-3 2xl:p-5 pb-[2px] 2xl:pb-[5px] bg-white rounded-2xl border border-[var(--neutral-color)]">
                        <div className='lg:col-start-4'>
                            <Input
                                type="text"
                                placeholder={t('filters.search_placeholder')}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                leftElement={searchIcon}
                            />
                        </div>
                        <div>
                            <Select
                                placeholder={t('filters.all_status')}
                                value={statusFilter}
                                onChange={setStatusFilter}
                                options={statusOptions}
                            />
                        </div>
                    </div>

                    {isError ? (
                        <div className="bg-white rounded-2xl border border-[var(--neutral-color)] p-4 text-sm text-[var(--semantic-error-color)]">
                            {t('errors.load_failed')}
                        </div>
                    ) : (
                        <DataGridTable
                            columns={columns}
                            data={cohorts}
                            loading={isLoading}
                            totalCount={cohortsResponse?.data?.total ?? 0}
                            pageIndex={pageIndex}
                            pageSize={PAGE_SIZE}
                            onPageChange={setPageIndex}
                        />
                    )}
                </div>

                <ConfirmModal
                    isOpen={pendingDeleteId !== null}
                    onClose={() => setPendingDeleteId(null)}
                    onConfirm={() => {
                        if (pendingDeleteId) {
                            deleteCohortMutation.mutate(pendingDeleteId);
                        }
                    }}
                    title={t('toast.delete_title')}
                    message={t('toast.delete_confirm')}
                    confirmLabel={t('actions.delete')}
                    variant="danger"
                    loading={deleteCohortMutation.isPending}
                />

                <AssignStudentsModal
                    isOpen={assignCohort !== null}
                    onClose={() => setAssignCohort(null)}
                    cohortId={assignCohort?.id ?? ''}
                    cohortName={assignCohort?.name ?? ''}
                />

                <CohortStudentsModal
                    isOpen={viewCohortStudents !== null}
                    onClose={() => setViewCohortStudents(null)}
                    cohortId={viewCohortStudents?.id ?? ''}
                    cohortName={viewCohortStudents?.name ?? ''}
                />
            </>
        </TableRowActionsTooltipProvider>
    );
};

export default CohortsPage;
